import { Box, Button, Group, Text } from '@optiaxiom/react';
import {
  IconArrowUpRightFromSquare,
  IconCheckCircleSolid,
  IconFileLines,
  IconTerminal,
} from '@optiaxiom/icons';
import { AppDialog } from '@/components/ui/AppDialog';
import { Terminal } from '@/components/ui/Terminal';
import type { RegisteredApp } from '@/lib/api';
import styles from './AppRegisteredModal.module.css';

const SCAFFOLD_DOCS_URL =
  'https://docs.developers.optimizely.com/optimizely-connect-platform/docs/registration-and-scaffolding#scaffold-the-app';

interface AppRegisteredModalProps {
  open: boolean;
  /** The app that was just registered, or null when the dialog is closed. */
  app: RegisteredApp | null;
  onClose: () => void;
}

/**
 * Shown after a successful registration. The portal only registers the app — the developer
 * scaffolds and builds it from the OCP CLI — so this confirms success and points to the
 * scaffold command (pre-filled with the new app's id + display name) + docs.
 */
export function AppRegisteredModal({ open, app, onClose }: AppRegisteredModalProps) {
  const command = app
    ? `ocp app init --app-id ${app.id} --display-name "${app.name}"`
    : 'ocp app init';

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="App registered"
      footer={
        <Button appearance="primary" onClick={onClose}>
          Got it
        </Button>
      }
    >
      <Group flexDirection="column" gap="20" alignItems="stretch">
        <Group flexDirection="row" gap="16" alignItems="center">
          <IconCheckCircleSolid color='green' size={28} />
          <Text color="fg.secondary">
            <strong>{app?.name ?? 'Your app'}</strong> has been registered successfully. The next step is
            to scaffold your app using the OCP CLI.
          </Text>
        </Group>

        <Box border="1" borderColor="border.default" rounded="lg" p="16">
          <Group flexDirection="row" gap="12" alignItems="start">
            <Box className={styles.stepIcon} bg="bg.secondary" color="fg.secondary">
              <IconTerminal size={20} />
            </Box>
            <Group flexDirection="column" gap="12" alignItems="stretch" className={styles.stepBody}>
              <Group flexDirection="column" gap="2" alignItems="start">
                <Text fontWeight="700">1. Scaffold your app</Text>
                <Text color="fg.secondary">
                  Run the following command in your terminal to get started.
                </Text>
              </Group>
              <Terminal lines={[{ text: command }]} copyAriaLabel="Copy scaffold command" />
            </Group>
          </Group>
        </Box>

        <Box border="1" borderColor="border.default" rounded="lg" p="16">
          <Group flexDirection="row" justifyContent="space-between" alignItems="center" gap="12">
            <Group flexDirection="row" gap="12" alignItems="start">
              <Box className={styles.stepIcon} bg="bg.secondary" color="fg.secondary">
                <IconFileLines size={20} />
              </Box>
              <Group flexDirection="column" gap="2" alignItems="start">
                <Text fontWeight="700">2. Learn more</Text>
                <Text color="fg.secondary">
                  Check out the documentation to learn more about scaffolding and building with OCP.
                </Text>
              </Group>
            </Group>
            <Button appearance="default" asChild addonBefore={<IconArrowUpRightFromSquare />}>
              <a href={SCAFFOLD_DOCS_URL} target="_blank" rel="noreferrer">
                View documentation
              </a>
            </Button>
          </Group>
        </Box>
      </Group>
    </AppDialog>
  );
}
